import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet, TouchableOpacity, View } from 'react-native';

import Colors from '../../../theming/Colors';
import Spacing from '../../../theming/Spacing';
import FlatListV2 from '../../FlatListV2';
import KitIcon from '../../KitIcon';
import KitInput from '../../KitInput';
import KitModalV2 from '../../KitModalV2/KitModalV2';
import KitText from '../../KitText';
import { Country } from '../constants/types';

// Height constants for modal sizing
const MODAL_HEIGHT_CONSTANTS = {
  ITEM_HEIGHT: 56,
  TITLE_HEIGHT: 50,
  SEARCH_HEIGHT: 60,
  VERTICAL_SPACING_MULTIPLIER: 3,
  MAX_HEIGHT_PERCENTAGE: 0.85, // Changed to number for easier calculations
};

interface CountryCodeSelectorModalProps {
  isVisible: boolean;
  onClose: () => void;
  countries: Country[];
  selectedCountry?: Country;
  onCountrySelect: (country: Country) => void;
}

// Memoized CountryItem component to prevent unnecessary re-renders
const CountryItem = React.memo(
  ({
    item,
    isSelected,
    onSelect,
  }: {
    item: Country;
    isSelected: boolean;
    onSelect: (country: Country) => void;
  }) => (
    <TouchableOpacity
      style={styles.countryItem}
      onPress={() => onSelect(item)}
      accessibilityRole='button'
      accessibilityLabel={`${item.name} ${item.callingCode}`}
    >
      <View style={styles.countryItemContent}>
        <KitText fontSize={20}>{item.flag}</KitText>
        <KitText fontSize={16} color={Colors.N1000} style={styles.countryName}>
          {item.name}
        </KitText>
        <KitText fontSize={16} color={Colors.N1000}>
          ({item.callingCode})
        </KitText>
      </View>
      {isSelected ? (
        <KitIcon name='check' size={20} color={Colors.N1000} />
      ) : null}
    </TouchableOpacity>
  )
);

const CountryCodeSelectorModal = ({
  isVisible,
  onClose,
  countries,
  selectedCountry,
  onCountrySelect,
}: CountryCodeSelectorModalProps): ReactElement => {
  // ------------------------------------------------------------
  // Context & Custom Hooks

  const { t } = useTranslation();
  const { sizeClassV2 } = useSizeClass();
  const anchorBottom = sizeClassV2 < SizeClassV2.L;

  // ------------------------------------------------------------
  // Refs

  const flatListRef = useRef<any>(null);
  const initialModalOpenRef = useRef(false);

  // ------------------------------------------------------------
  // State

  const [searchQuery, setSearchQuery] = useState('');

  // ------------------------------------------------------------
  // Computed Values

  const shouldShowSearch = useMemo(() => {
    // Calculate modal height WITHOUT search to determine if list would be scrollable
    const verticalSpacing =
      Spacing.l * MODAL_HEIGHT_CONSTANTS.VERTICAL_SPACING_MULTIPLIER;
    const baseModalHeight =
      MODAL_HEIGHT_CONSTANTS.TITLE_HEIGHT + verticalSpacing;

    // Get screen height for percentage calculations
    const screenHeight = Dimensions.get('window').height;
    const maxModalHeight = screenHeight * 0.85; // 85% of screen height

    // Calculate available height for the list (without search)
    const availableListHeight = maxModalHeight - baseModalHeight;

    // Calculate total content height needed for all countries
    const totalContentHeight =
      countries.length * MODAL_HEIGHT_CONSTANTS.ITEM_HEIGHT;

    // Show search if content exceeds available space
    return totalContentHeight > availableListHeight;
  }, [countries.length]);

  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return countries;

    const query = searchQuery.toLowerCase();

    return countries
      .filter((country) => {
        return (
          country.name.toLowerCase().includes(query) ||
          country.callingCode.includes(query) ||
          country.regionCode.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();

        // Check if name starts with the query
        const aStartsWith = aName.startsWith(query);
        const bStartsWith = bName.startsWith(query);

        if (aStartsWith && !bStartsWith) return -1;

        if (!aStartsWith && bStartsWith) return 1;

        return 0;
      });
  }, [countries, searchQuery]);

  const selectedCountryIndex = useMemo(() => {
    if (!selectedCountry) return -1;

    return filteredCountries.findIndex(
      (country) => country.regionCode === selectedCountry.regionCode
    );
  }, [selectedCountry, filteredCountries]);

  // Calculate dynamic modal height
  const modalHeight = useMemo(() => {
    const searchHeight = shouldShowSearch
      ? MODAL_HEIGHT_CONSTANTS.SEARCH_HEIGHT
      : 0;
    const verticalSpacing =
      Spacing.l * MODAL_HEIGHT_CONSTANTS.VERTICAL_SPACING_MULTIPLIER;

    // Get screen dimensions
    const screenHeight = Dimensions.get('window').height;
    const maxAllowedHeight =
      screenHeight * MODAL_HEIGHT_CONSTANTS.MAX_HEIGHT_PERCENTAGE;

    // Calculate base height (title + search + spacing)
    const baseHeight =
      MODAL_HEIGHT_CONSTANTS.TITLE_HEIGHT + searchHeight + verticalSpacing;

    // Calculate available height for the list
    const availableListHeight = maxAllowedHeight - baseHeight;

    // Calculate maximum number of items that can fit
    const maxVisibleItems = Math.floor(
      availableListHeight / MODAL_HEIGHT_CONSTANTS.ITEM_HEIGHT
    );

    // Determine actual visible items
    const visibleItems = Math.min(countries.length, maxVisibleItems);
    const listHeight = visibleItems * MODAL_HEIGHT_CONSTANTS.ITEM_HEIGHT;

    const calculatedHeight = baseHeight + listHeight;

    // If all countries fit within the calculated height, use exact height
    // Otherwise, use the max allowed height to enable scrolling
    if (countries.length <= maxVisibleItems) {
      return calculatedHeight;
    }

    return maxAllowedHeight;
  }, [countries.length, shouldShowSearch]);

  // Calculate the scrollable list height for centering
  const scrollableListHeight = useMemo(() => {
    const searchHeight = shouldShowSearch
      ? MODAL_HEIGHT_CONSTANTS.SEARCH_HEIGHT
      : 0;
    const verticalSpacing =
      Spacing.l * MODAL_HEIGHT_CONSTANTS.VERTICAL_SPACING_MULTIPLIER;
    const baseHeight =
      MODAL_HEIGHT_CONSTANTS.TITLE_HEIGHT + searchHeight + verticalSpacing;

    return modalHeight - baseHeight;
  }, [modalHeight, shouldShowSearch]);

  // ------------------------------------------------------------
  // Side Effects

  useEffect(() => {
    if (
      isVisible &&
      selectedCountryIndex >= 0 &&
      !initialModalOpenRef.current
    ) {
      // Mark that we've handled the initial modal open
      initialModalOpenRef.current = true;

      // Simple delay to ensure modal is rendered
      const timeoutId = setTimeout(() => {
        // Use FlatListV2's unified scrollToIndex method
        flatListRef.current?.scrollToIndex({
          index: selectedCountryIndex,
          animated: false,
          viewPosition: 0.5,
        });
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [
    isVisible,
    selectedCountryIndex,
    filteredCountries,
    scrollableListHeight,
  ]);

  // ------------------------------------------------------------
  // Event Handlers & Callbacks

  const handleModalClose = useCallback(() => {
    onClose();
    setSearchQuery('');
    // Reset the initial modal open flag for next time
    initialModalOpenRef.current = false;
  }, [onClose]);

  const selectCountry = useCallback(
    (country: Country): void => {
      onCountrySelect(country);
      setSearchQuery('');
    },
    [onCountrySelect]
  );

  const handleScrollToIndexFailed = useCallback(
    (info: {
      index: number;
      highestMeasuredFrameIndex: number;
      averageItemLength: number;
    }) => {
      // Fallback to scrollToOffset when scrollToIndex fails
      const offset = info.index * MODAL_HEIGHT_CONSTANTS.ITEM_HEIGHT;
      const centeredOffset = Math.max(0, offset - scrollableListHeight / 2);

      flatListRef.current?.scrollToOffset({
        offset: centeredOffset,
        animated: false,
      });
    },
    [scrollableListHeight]
  );

  // ------------------------------------------------------------
  // Render Helpers

  const renderEmptyState = useCallback(
    () => (
      <View style={styles.emptyState}>
        <KitText
          fontSize={16}
          color={Colors.N500}
          style={styles.emptyStateText}
        >
          {searchQuery.trim()
            ? t('auth:noCountriesFound')
            : t('auth:noCountriesAvailable')}
        </KitText>
      </View>
    ),
    [searchQuery, t]
  );

  const getItemLayout = useCallback(
    (_data: ArrayLike<Country> | null | undefined, index: number) => ({
      length: MODAL_HEIGHT_CONSTANTS.ITEM_HEIGHT,
      offset: MODAL_HEIGHT_CONSTANTS.ITEM_HEIGHT * index,
      index,
    }),
    []
  );

  const renderCountryItem = useCallback(
    ({ item }: { item: Country }) => (
      <CountryItem
        item={item}
        isSelected={selectedCountry?.regionCode === item.regionCode}
        onSelect={selectCountry}
      />
    ),
    [selectCountry, selectedCountry?.regionCode]
  );

  return (
    <KitModalV2
      anchorBottom={anchorBottom}
      coverScreen
      noScrollView
      enableScroll={false}
      isVisible={isVisible}
      onClose={handleModalClose}
      modalStyle={{
        ...styles.modal,
        height: modalHeight,
        width: anchorBottom ? '100%' : 700,
      }}
    >
      <View style={styles.modalContainer}>
        <KitText
          style={styles.modalTitle}
          h1
          color={Colors.N900}
          testID='modalPhoneNumberTitle'
        >
          {t('auth:selectYourCountryCode')}
        </KitText>

        {shouldShowSearch ? (
          <View style={styles.searchContainer}>
            <KitInput
              placeholder={t('auth:searchCountries')}
              placeholderTextColor={Colors.N500}
              value={searchQuery}
              onChangeText={setSearchQuery}
              leftIcon={<KitIcon name='search' size={20} color={Colors.N500} />}
              testID='countrySearchInput'
            />
          </View>
        ) : null}

        <View style={styles.listContainer}>
          <FlatListV2
            ref={flatListRef}
            data={filteredCountries}
            renderItem={renderCountryItem}
            keyExtractor={(item: Country) => item.regionCode}
            ListEmptyComponent={renderEmptyState}
            showsVerticalScrollIndicator
            testID='countriesList'
            accessibilityLabel='Countries list'
            getItemLayout={getItemLayout}
            maxToRenderPerBatch={20}
            initialNumToRender={20}
            windowSize={10}
            updateCellsBatchingPeriod={50}
            onScrollToIndexFailed={handleScrollToIndexFailed}
          />
        </View>
      </View>
    </KitModalV2>
  );
};

const styles = StyleSheet.create({
  modal: {
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  countryItem: {
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.l,
    minHeight: MODAL_HEIGHT_CONSTANTS.ITEM_HEIGHT,
  },
  countryItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
  },
  countryName: {
    fontSize: 14,
  },
  modalContainer: {
    gap: Spacing.l,
    flex: 1,
  },
  modalTitle: {
    paddingHorizontal: Spacing.l,
  },
  searchContainer: {
    paddingHorizontal: Spacing.l,
  },
  listContainer: {
    flex: 1,
  },
  emptyState: {
    padding: Spacing.xl,
    alignItems: 'center',
  },
  emptyStateText: {
    textAlign: 'center',
  },
});

export default CountryCodeSelectorModal;
